import React, { useState, useEffect } from 'react';
import { Play, Pause, Trash2, RefreshCw, Music as MusicIcon, AlertTriangle, RotateCcw, Search } from 'lucide-react';
import api from '../../services/api';

export default function MusicTab() {
  const [tracks, setTracks] = useState([]);
  const [pending, setPending] = useState([]);
  const [failed, setFailed] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracks');
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tracksResponse, pendingResponse, failedResponse, statsResponse] = await Promise.all([
        api.get('/music/tracks'),
        api.get('/music/pending'),
        api.get('/music/failed'),
        api.get('/music/stats')
      ]);

      setTracks(tracksResponse.data.tracks || []);
      setPending(pendingResponse.data || []);
      setFailed(failedResponse.data.tracks || []);
      setStats(statsResponse.data || null);
    } catch (error) {
      console.error('שגיאה בטעינת נתוני מוזיקה:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/music/settings');
      setSettings(response.data || {});
    } catch (error) {
      console.error('שגיאה בטעינת הגדרות:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const source = newSettings.channel_source || 'Hasidim_music';
      const syncedSettings = {
        ...newSettings,
        channel_username: source === 'music_ms2' ? 'music_ms2' : 'Hasidim_music'
      };
      const response = await api.put('/music/settings', syncedSettings);

      if (response.data?.success) {
        setSettings(syncedSettings);
        alert('הגדרות עודכנו בהצלחה');
      } else {
        alert('שגיאה בעדכון הגדרות');
      }
    } catch (error) {
      console.error('שגיאה בעדכון הגדרות:', error);
      alert('שגיאה בעדכון הגדרות');
    }
  };

  const deleteTrack = async (trackId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק שיר זה?')) return;

    try {
      const response = await api.delete(`/music/tracks/${trackId}`);

      if (response.data?.success) {
        setTracks(tracks.filter(track => track.id !== trackId));
        alert('שיר נמחק בהצלחה');
      } else {
        alert('שגיאה במחיקת שיר');
      }
    } catch (error) {
      console.error('שגיאה במחיקת שיר:', error);
      alert('שגיאה במחיקת שיר');
    }
  };

  const scanNow = async () => {
    try {
      const response = await api.post('/music/scan');
      if (response.data?.success) {
        alert('סריקת מוזיקה הופעלה! התוצאות יופיעו בעוד כ-10 שניות. לחץ על רענון לאחר מכן.');
      } else {
        alert('שגיאה בהפעלת סריקה');
      }
    } catch (error) {
      console.error('שגיאה בסריקה:', error);
      alert('שגיאה בהפעלת סריקה');
    }
  };

  const retryTrack = async (trackId) => {
    try {
      const response = await api.post(`/music/retry/${trackId}`);
      if (response.data?.success) {
        setFailed(failed.filter(t => t.id !== trackId));
        alert('השיר יעובד מחדש בסריקה הבאה (עד 5 דקות)');
      } else {
        alert('שגיאה בביצוע retry');
      }
    } catch (error) {
      console.error('שגיאה ב-retry:', error);
      alert('שגיאה בביצוע retry');
    }
  };

  const playAudio = (track) => {
    if (playing === track.id) {
      setPlaying(null);
      // TODO: עצירת נגן
    } else {
      setPlaying(track.id);
      // TODO: הפעלת נגן עם track.audio_url
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'uploaded': return 'text-blue-600 bg-blue-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'לא ידוע';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'לא ידוע';
    return new Date(dateString).toLocaleString('he-IL');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="mr-3 text-gray-600">טוען נתוני מוזיקה...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">ניהול מוזיקה</h2>
          <p className="text-gray-600">ניהול שירים ותצורת ערוץ המוזיקה</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={scanNow}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Search className="w-4 h-4 ml-2" />
            סרוק עכשיו
          </button>
          <button
            onClick={loadData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 ml-2" />
            רענון
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">סה"כ שירים</div>
            <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">נשלחו בהצלחה</div>
            <div className="text-2xl font-bold text-green-900">{stats.sent}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-sm text-red-600 font-medium">נכשלו</div>
            <div className="text-2xl font-bold text-red-900">{stats.failed}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-sm text-yellow-600 font-medium">הועלו (ממתין ל-API)</div>
            <div className="text-2xl font-bold text-yellow-900">{stats.uploaded}</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium">הודעות מידע</div>
            <div className="text-2xl font-bold text-purple-900">{stats.pending_info}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto whitespace-nowrap">
          <button
            onClick={() => setActiveTab('tracks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tracks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            שירים מעובדים ({tracks.length})
          </button>
          <button
            onClick={() => setActiveTab('failed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'failed'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1">
              {failed.length > 0 && <AlertTriangle className="w-4 h-4" />}
              שגיאות ({failed.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            הודעות ממתינות ({pending.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            הגדרות
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'tracks' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">שירים מעובדים</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שיר
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    אמן
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    גודל קובץ
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תאריך
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tracks.map((track) => (
                  <tr key={track.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => playAudio(track)}
                          className="p-1 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                        >
                          {playing === track.id ? (
                            <Pause className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Play className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                        <div className="mr-3">
                          <div className="text-sm font-medium text-gray-900">
                            {track.title || 'ללא כותרת'}
                          </div>
                          {track.youtube_url && (
                            <a
                              href={track.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              YouTube
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {track.artist || 'לא ידוע'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(track.status)}`}>
                        {track.status === 'sent' ? 'נשלח' :
                         track.status === 'failed' ? 'נכשל' :
                         track.status === 'uploaded' ? 'הועלה' : 'ממתין'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(track.file_size_bytes)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(track.created_at)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => deleteTrack(track.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {tracks.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <MusicIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>אין שירים מעובדים עדיין</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'failed' && (
        <div className="bg-white rounded-lg shadow-sm border border-red-200">
          <div className="px-4 sm:px-6 py-4 border-b border-red-200 bg-red-50">
            <h3 className="text-lg font-medium text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              שירים שנכשלו
            </h3>
            <p className="text-sm text-red-700">שירים שנכשלו בהעלאה או בשליחה ל-API. הבוט ינסה אותם מחדש אוטומטית בסריקה הבאה.</p>
          </div>

          <div className="overflow-x-auto">
            {failed.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">כותרת</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">אמן</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">שגיאה</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">תאריך</th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">פעולות</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {failed.map(track => (
                    <tr key={track.id} className="hover:bg-red-50">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{track.id}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {track.title || 'ללא כותרת'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {track.artist || '-'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-red-600 max-w-xs truncate" title={track.error_message}>
                        {track.error_message || 'שגיאה לא ידועה'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(track.created_at)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => retryTrack(track.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="נסה שוב"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTrack(track.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="מחק"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-green-500 text-4xl mb-4">&#10003;</div>
                <p className="text-green-700 font-medium">אין שגיאות! כל השירים הועלו בהצלחה.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">הודעות מידע ממתינות</h3>
            <p className="text-sm text-gray-600">הודעות שמחכות לקובץ אודיו מתאים</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    כותרת
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    אמן
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תוכן
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תאריך
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pending.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.title || 'ללא כותרת'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.artist || 'לא ידוע'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {item.info_text?.substring(0, 100) || 'ללא תוכן'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pending.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>אין הודעות ממתינות</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">הגדרות מודול מוזיקה</h3>
          </div>

          <div className="p-4 sm:p-6 space-y-6">
            {/* בחירת ערוץ מוזיקה */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ערוץ מקור מוזיקה
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setSettings({...settings, channel_source: 'Hasidim_music', channel_username: 'Hasidim_music'})}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    (settings.channel_source || 'Hasidim_music') === 'Hasidim_music'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Hasidim_music
                </button>
                <button
                  type="button"
                  onClick={() => setSettings({...settings, channel_source: 'music_ms2', channel_username: 'music_ms2'})}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    settings.channel_source === 'music_ms2'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  music_ms2
                </button>
              </div>
              {settings.channel_source === 'music_ms2' && (
                <p className="mt-2 text-xs text-gray-500">
                  בערוץ זה יעובדו רק הודעות עם קישור ישיר לשיר ביוטיוב (youtu.be/). קישורים לערוצים ופרסומות ידולגו.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם ערוץ טלגרם
                </label>
                <input
                  type="text"
                  value={settings.channel_username || ''}
                  onChange={(e) => setSettings({...settings, channel_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  כמות הודעות ייבוא ראשוני
                </label>
                <input
                  type="number"
                  value={settings.initial_import_count || 20}
                  onChange={(e) => setSettings({...settings, initial_import_count: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL API חיצוני
              </label>
              <input
                type="url"
                value={settings.api_endpoint || ''}
                onChange={(e) => setSettings({...settings, api_endpoint: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auth Token
              </label>
              <input
                type="password"
                value={settings.auth_token || ''}
                onChange={(e) => setSettings({...settings, auth_token: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL העלאת אודיו
              </label>
              <input
                type="url"
                value={settings.audio_upload_url || ''}
                onChange={(e) => setSettings({...settings, audio_upload_url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                בסיס URL ציבורי לאודיו
              </label>
              <input
                type="url"
                value={settings.audio_public_base || ''}
                onChange={(e) => setSettings({...settings, audio_public_base: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                checked={settings.enabled || false}
                onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="mr-2 block text-sm text-gray-900">
                הפעל מודול מוזיקה
              </label>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => updateSettings(settings)}
                className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                שמור הגדרות
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
